<template>
  <!-- Farbauswahl der Editor-Toolbar (Kundenfeedback 24.10.2026, IV.8).
       Alle Knöpfe verhindern mousedown: Der Editor behält Fokus und
       Markierung, die Farbe wirkt genau auf den markierten Text. -->
  <div ref="root" class="ecm">
    <button
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="title"
      class="ecm__trigger"
      :class="{ 'ecm__trigger--open': open }"
      :data-testid="testid"
      :title="title"
      type="button"
      @click="toggle"
      @mousedown.prevent
    >
      <slot />
      <span class="ecm__bar" :style="{ background: current === noneValue ? 'transparent' : current }" />
    </button>
    <div v-if="open" :aria-label="title" class="ecm__menu" role="menu">
      <div class="ecm__grid">
        <button
          v-for="c in colors"
          :key="c.value"
          :aria-label="$gt(c.label)"
          class="ecm__swatch"
          :class="{ 'ecm__swatch--on': c.value === current }"
          :data-testid="`${testid}-${c.value.slice(1)}`"
          role="menuitem"
          :style="{ background: c.value }"
          :title="$gt(c.label)"
          type="button"
          @click="pick(c.value)"
          @mousedown.prevent
        />
      </div>
      <button
        v-if="noneLabel"
        class="ecm__row"
        role="menuitem"
        type="button"
        @click="pick(noneValue)"
        @mousedown.prevent
      >
        <span class="ecm__none-icon" />{{ noneLabel }}
      </button>
      <label class="ecm__row" :title="$gt('Eigene Farbe wählen')">
        <Palette :size="14" />{{ $gt('Weitere Farbe…') }}
        <input class="ecm__native" type="color" :value="current === noneValue ? '#ffff00' : current" @change="pick(($event.target as HTMLInputElement).value)">
      </label>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { EditorColor } from '@/utils/editorHighlight'
  import { Palette } from 'lucide-vue-next'
  import { onBeforeUnmount, ref, watch } from 'vue'
  import { NO_HIGHLIGHT } from '@/utils/editorHighlight'

  defineProps<{
    title: string
    colors: readonly EditorColor[]
    current: string
    /** Beschriftung für „Farbe entfernen"; ohne Wert entfällt die Zeile. */
    noneLabel?: string
    testid?: string
  }>()
  const emit = defineEmits<{ open: [], pick: [color: string] }>()

  const noneValue = NO_HIGHLIGHT
  const open = ref(false)
  const root = ref<HTMLElement | null>(null)

  function toggle () {
    if (!open.value) emit('open')
    open.value = !open.value
  }

  function pick (color: string) {
    open.value = false
    emit('pick', color)
  }

  function onDocMouseDown (ev: MouseEvent) {
    if (root.value && !root.value.contains(ev.target as Node)) open.value = false
  }

  watch(open, isOpen => {
    if (isOpen) document.addEventListener('mousedown', onDocMouseDown)
    else document.removeEventListener('mousedown', onDocMouseDown)
  })
  onBeforeUnmount(() => document.removeEventListener('mousedown', onDocMouseDown))
</script>

<style scoped>
.ecm { position: relative; display: inline-flex; }
.ecm__trigger {
  position: relative;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 2px;
  width: 30px; height: 30px;
  border: none; border-radius: 6px;
  background: none; color: var(--grey-color);
  cursor: pointer; transition: background-color 0.1s, color 0.1s;
}
.ecm__trigger:hover,
.ecm__trigger--open { background: var(--grey-light-color); color: var(--dark-color); }
.ecm__trigger:focus-visible { outline: 2px solid var(--primary-color); outline-offset: 1px; }
.ecm__bar { width: 16px; height: 4px; border-radius: 1px; border: 1px solid rgba(15, 23, 42, 0.15); }
.ecm__menu {
  position: absolute; top: calc(100% + 4px); left: 0; z-index: 30;
  display: flex; flex-direction: column; gap: 6px;
  min-width: 176px; padding: 10px;
  border: 1px solid var(--border-grey-color); border-radius: 10px;
  background: #fff; box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}
.ecm__grid { display: grid; grid-template-columns: repeat(6, 22px); gap: 6px; }
.ecm__swatch {
  width: 22px; height: 22px; padding: 0;
  border: 1px solid rgba(15, 23, 42, 0.2); border-radius: 5px;
  cursor: pointer; transition: transform 0.1s;
}
.ecm__swatch:hover { transform: scale(1.12); }
.ecm__swatch:focus-visible,
.ecm__swatch--on { outline: 2px solid var(--primary-color); outline-offset: 1px; }
.ecm__row {
  position: relative;
  display: flex; align-items: center; gap: 8px;
  padding: 5px 6px; border: none; border-radius: 6px;
  background: none; font-size: 0.78rem; color: var(--dark-color);
  cursor: pointer; text-align: left;
}
.ecm__row:hover { background: var(--grey-light-color); }
.ecm__none-icon {
  width: 14px; height: 14px; border: 1px solid var(--grey-color); border-radius: 3px;
  background: linear-gradient(135deg, transparent 45%, #c62828 45%, #c62828 55%, transparent 55%);
}
.ecm__native { position: absolute; inset: 0; width: 100%; height: 100%; opacity: 0; cursor: pointer; }
</style>
